// Comprehensive Theme Configuration
// All colors and design tokens used across the application

export const colors = {
  // Background colors
  bg: {
    primary: {
      light: '#f5f7fb',
      dark: '#0a0e1a',
    },
    secondary: {
      light: '#ffffff',
      dark: '#141923',
    },
    hover: {
      light: 'rgba(15, 23, 42, 0.04)',
      dark: 'rgba(255, 255, 255, 0.08)',
    },
    subtle: {
      light: 'rgba(15, 23, 42, 0.01)',
      dark: 'rgba(255, 255, 255, 0.02)',
    },
  },

  // Text colors
  text: {
    primary: {
      light: '#0f172a',
      dark: '#ffffff',
    },
    secondary: {
      light: 'rgba(15, 23, 42, 0.75)',
      dark: 'rgba(255, 255, 255, 0.85)',
    },
    muted: {
      light: 'rgba(15, 23, 42, 0.65)',
      dark: 'rgba(255, 255, 255, 0.6)',
    },
  },

  // Border colors
  border: {
    default: {
      light: 'rgba(15, 23, 42, 0.12)',
      dark: 'rgba(255, 255, 255, 0.12)',
    },
    light: {
      light: 'rgba(15, 23, 42, 0.08)',
      dark: 'rgba(255, 255, 255, 0.08)',
    },
  },

  // Shadow colors
  shadow: {
    light: 'rgba(15, 23, 42, 0.08)',
    dark: 'rgba(0, 0, 0, 0.6)',
  },

  // Accent colors - Primary palette
  accent: {
    blue: {
      light: '#2563eb',
      dark: '#60a5fa',
      50: '#eff6ff',
      100: '#dbeafe',
      200: '#bfdbfe',
      300: '#93c5fd',
      400: '#60a5fa',
      500: '#3b82f6',
      600: '#2563eb',
      700: '#1d4ed8',
      800: '#1e40af',
      900: '#1e3a8a',
    },
    purple: {
      light: '#7c3aed',
      dark: '#a78bfa',
      50: '#faf5ff',
      100: '#f3e8ff',
      200: '#e9d5ff',
      300: '#d8b4fe',
      400: '#c084fc',
      500: '#a855f7',
      600: '#9333ea',
      700: '#7c3aed',
      800: '#6b21a8',
      900: '#581c87',
    },
    green: {
      light: '#22c55e',
      dark: '#4ade80',
      50: '#f0fdf4',
      100: '#dcfce7',
      200: '#bbf7d0',
      300: '#86efac',
      400: '#4ade80',
      500: '#22c55e',
      600: '#16a34a',
      700: '#15803d',
      800: '#166534',
      900: '#14532d',
    },
    red: {
      light: '#ef4444',
      dark: '#f87171',
      50: '#fef2f2',
      100: '#fee2e2',
      200: '#fecaca',
      300: '#fca5a5',
      400: '#f87171',
      500: '#ef4444',
      600: '#dc2626',
      700: '#b91c1c',
      800: '#991b1b',
      900: '#7f1d1d',
    },
    orange: {
      light: '#f97316',
      dark: '#fb923c',
      50: '#fff7ed',
      100: '#ffedd5',
      200: '#fed7aa',
      300: '#fdba74',
      400: '#fb923c',
      500: '#f97316',
      600: '#ea580c',
      700: '#c2410c',
      800: '#9a3412',
      900: '#7c2d12',
    },
    yellow: {
      light: '#eab308',
      dark: '#fbbf24',
      50: '#fefce8',
      100: '#fef9c3',
      200: '#fef08a',
      300: '#fde047',
      400: '#facc15',
      500: '#eab308',
      600: '#ca8a04',
      700: '#a16207',
      800: '#854d0e',
      900: '#713f12',
    },
    pink: {
      light: '#ec4899',
      dark: '#f472b6',
      500: '#ec4899',
      600: '#db2777',
    },
  },

  // Semantic colors
  success: {
    main: '#22c55e',
    light: '#4ade80',
    dark: '#16a34a',
    bg: 'rgba(34, 197, 94, 0.15)',
    border: 'rgba(34, 197, 94, 0.3)',
  },
  error: {
    main: '#ef4444',
    light: '#f87171',
    dark: '#dc2626',
    bg: 'rgba(239, 68, 68, 0.15)',
    border: 'rgba(239, 68, 68, 0.3)',
  },
  warning: {
    main: '#f59e0b',
    light: '#fbbf24',
    dark: '#d97706',
    bg: 'rgba(250, 204, 21, 0.18)',
    border: 'rgba(250, 204, 21, 0.4)',
  },
  info: {
    main: '#3b82f6',
    light: '#60a5fa',
    dark: '#2563eb',
    bg: 'rgba(37, 99, 235, 0.12)',
    border: 'rgba(37, 99, 235, 0.3)',
  },

  // Neutral colors (slate-based)
  neutral: {
    50: '#f8fafc',
    100: '#f1f5f9',
    200: '#e2e8f0',
    300: '#cbd5e1',
    400: '#94a3b8',
    500: '#64748b',
    600: '#475569',
    700: '#334155',
    800: '#1e293b',
    900: '#0f172a',
    950: '#020617',
  },

  // Common colors (black, white - used for Apple button, etc.)
  common: {
    black: '#000000',
    white: '#ffffff',
    blackHover: '#1a1a1a',
  },

  // Brand colors for special cards
  brand: {
    netflix: '#e50914',
    warner: '#004c97',
    visa: '#1434CB',
    google: {
      blue: '#4285F4',
      green: '#34A853',
      yellow: '#FBBC05',
      red: '#EA4335',
    },
    whatsapp: '#25D366',
  },
} as const;

// Spacing scale (matching Tailwind)
export const spacing = {
  px: '1px',
  0: '0',
  0.5: '0.125rem',
  1: '0.25rem',
  1.5: '0.375rem',
  2: '0.5rem',
  2.5: '0.625rem',
  3: '0.75rem',
  3.5: '0.875rem',
  4: '1rem',
  5: '1.25rem',
  6: '1.5rem',
  7: '1.75rem',
  8: '2rem',
  9: '2.25rem',
  10: '2.5rem',
  12: '3rem',
  14: '3.5rem',
  16: '4rem',
  20: '5rem',
  24: '6rem',
} as const;

// Border radius
export const borderRadius = {
  none: '0',
  sm: '0.125rem',
  DEFAULT: '0.25rem',
  md: '0.375rem',
  lg: '0.5rem',
  xl: '0.75rem',
  '2xl': '1rem',
  '3xl': '1.5rem',
  full: '9999px',
} as const;

// Font sizes
export const fontSize = {
  xs: ['0.75rem', { lineHeight: '1rem' }],
  sm: ['0.875rem', { lineHeight: '1.25rem' }],
  base: ['1rem', { lineHeight: '1.5rem' }],
  lg: ['1.125rem', { lineHeight: '1.75rem' }],
  xl: ['1.25rem', { lineHeight: '1.75rem' }],
  '2xl': ['1.5rem', { lineHeight: '2rem' }],
  '3xl': ['1.875rem', { lineHeight: '2.25rem' }],
  '4xl': ['2.25rem', { lineHeight: '2.5rem' }],
} as const;

// Font weights
export const fontWeight = {
  normal: '400',
  medium: '500',
  semibold: '600',
  bold: '700',
} as const;

// Box shadows
export const boxShadow = {
  sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  DEFAULT: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
  card: '0 4px 20px rgba(15, 23, 42, 0.08)',
  cardHover: '0 8px 30px rgba(15, 23, 42, 0.12)',
  button: '0 4px 16px rgba(37, 99, 235, 0.4)',
  success: '0 4px 12px rgba(34, 197, 94, 0.35)',
  error: '0 4px 12px rgba(239, 68, 68, 0.35)',
} as const;

// Transitions
export const transition = {
  fast: '0.15s ease',
  DEFAULT: '0.2s ease',
  slow: '0.3s ease',
} as const;

// Z-index scale
export const zIndex = {
  dropdown: 1000,
  modal: 2000,
  tooltip: 3000,
} as const;

// Breakpoints (matching Tailwind)
export const breakpoints = {
  sm: '640px',
  md: '768px',
  lg: '1024px',
  xl: '1280px',
  '2xl': '1536px',
} as const;

// Export type for theme mode
export type ThemeMode = 'light' | 'dark';

// Helper to get color for current theme
export const getThemedColor = (
  colorPath: { light: string; dark: string },
  mode: ThemeMode
): string => {
  return mode === 'dark' ? colorPath.dark : colorPath.light;
};
